import { DOCUMENT } from '@angular/common';
import { computed, DestroyRef, effect, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { firstValueFrom, forkJoin, from, Observable, of, Subject, Subscription } from 'rxjs';

// =============== APP-SPECIFIC IMPORTS ===============
import { getDownloadURL, ref, Storage } from '@angular/fire/storage';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, filter, map, switchMap, take } from 'rxjs/operators';
import { NotEnoughCreditComponent } from 'src/app/shared/components/snackbar/not-enough-credit/not-enough-credit.component';
import { ErrorHandlerService } from 'src/app/shared/services/error-handler.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { UploadParams, UploadService } from 'src/app/shared/services/upload/upload.service';
import { environment } from 'src/environments/environment';
import { v4 as uuidv4 } from 'uuid';
import { AppService } from '../../app.service';
import { AuthService } from '../../auth.service';
import { EnvService } from '../../env.service';
import { NotificationService } from '../../notification.service';
import { FirestoreEventBusService } from '../../shared/services/firestore-event-bus.service';
import { createSilentAudio } from '../../shared/utils/createSilentAudio';
import { StorageService } from '../../storage.service';
import { CourseDisplayerService } from '../displayer/courses/course-displayer/course-displayer.service';
import { MySpaceService } from '../my-space/my-space.service';
import { Chunk, CHUNKS_TO_SKIP_AUDIO_GEN, CHUNKS_WITH_AUDIO_PLAYER, CHUNKS_WITHOUT_AUDIO_PLAYER, SILENT_CHUNKS } from './chunk/chunk.dto';
import { ChunkService } from './chunk/chunk.service';
import { ContentChunkRef } from './content.dto';
import { ContentService } from './content.service';
import { ConversationService } from './conversation.service';

// =============== TYPE DEFINITIONS ===============

export interface LoadingState {
  status: 'idle' | 'loading' | 'success' | 'error';
  message?: string;
  reason?: string;
}

export type UploadStatus = 'uploading' | 'creating' | 'failed';

export interface UploadState {
  progress: number;
  status: UploadStatus;
  insertId: string | null;
  fileName: string;
}

export interface PlayerState {
  status: 'playing' | 'paused' | 'stopped' | 'buffering';
  sourceUrl?: SafeResourceUrl;
  chunkId?: string;
  currentTime?: number;
  duration?: number;
  playbackRate?: number;
}

export interface PlaybackCommand {
  chunkId: string;
  action: 'play' | 'pause';
}

export interface AIPaneData {
  chunkAboveId: string;
  chunkBelowId: string;
}

@Injectable({
  providedIn: 'root',
})
export class ContentStateService {
  private readonly appService = inject(AppService);
  private readonly contentService = inject(ContentService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly mySpaceService = inject(MySpaceService);
  private readonly chunkService = inject(ChunkService);
  private readonly notificationService = inject(NotificationService);
  private readonly authService = inject(AuthService);
  private readonly envService = inject(EnvService);
  private readonly cdService = inject(CourseDisplayerService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly storageService = inject(StorageService);
  private readonly translate = inject(TranslateService);
  private readonly fireStorage = inject(Storage);
  private readonly uploadService = inject(UploadService);
  private readonly conversationService = inject(ConversationService);
  private readonly firestoreEventBus = inject(FirestoreEventBusService);
  private readonly toast = inject(ToastService);
  private readonly errorHandler = inject(ErrorHandlerService);
  private isFetchingPieces = false;

  // =============== COMMAND CHANNELS ===============
  private readonly mainPlayerCommand = new Subject<'clear'>();
  readonly mainPlayerCommand$ = this.mainPlayerCommand.asObservable();

  private readonly _openAIPaneCommand = new Subject<AIPaneData>();
  readonly openAIPaneCommand$ = this._openAIPaneCommand.asObservable();

  private readonly _addTextToEditorCommand = new Subject<string>();
  readonly addTextToEditorCommand$ = this._addTextToEditorCommand.asObservable();

  private readonly _createChunkWithTextCommand = new Subject<string>();
  readonly createChunkWithTextCommand$ = this._createChunkWithTextCommand.asObservable();

  private readonly _openCanvasCommand = new Subject<any>();
  readonly openCanvasCommand$ = this._openCanvasCommand.asObservable();

  private readonly _openWhiteboardEditorCommand = new Subject<any>();
  readonly openWhiteboardEditorCommand$ = this._openWhiteboardEditorCommand.asObservable();

  private readonly _openQuestionsAnalysisSidebarCommand = new Subject<boolean>();
  readonly openQuestionsAnalysisSidebarCommand$ = this._openQuestionsAnalysisSidebarCommand.asObservable();

  private readonly _openQuestionsSearchSidebarCommand = new Subject<boolean>();
  readonly openQuestionsSearchSidebarCommand$ = this._openQuestionsSearchSidebarCommand.asObservable();

  // =============== PRIVATE STATE SIGNALS ===============
  private readonly _content: WritableSignal<Chunk[]> = signal([]);
  private readonly _loading: WritableSignal<LoadingState> = signal({ status: 'idle' });
  private readonly _lectureFinished = signal(false);
  private readonly _playerState: WritableSignal<PlayerState> = signal({ status: 'stopped' });
  private readonly _currentChunk: WritableSignal<Chunk | undefined> = signal(undefined);
  private readonly _playerCurrentTime: WritableSignal<number> = signal(0);
  private readonly _highlightedChunkId = signal<string | undefined>(undefined);
  private readonly _playbackCommand = signal<PlaybackCommand | undefined>(undefined);
  private readonly _uploadState = signal<UploadState | null>(null);
  private readonly _liveTranscriptionSource = signal<'recorder' | 'chunk' | 'dictation' | null>(null);
  private readonly _isReader = signal<boolean>(false);
  private readonly _isContentEditor = signal<boolean>(false);
  private readonly _questionsAnalysisResult = signal<any>(null);
  private readonly _isSearchOpen = signal<boolean>(false);
  private readonly _searchResults = signal<any>(null);
  private readonly _activeTasks = signal<Record<string, any>>({});
  private readonly _selectedChunks = signal<string[]>([]);
  private readonly _isMultiSelectMode = signal<boolean>(false);

  // =============== PUBLIC READ-ONLY SIGNALS ===============
  readonly activeTasks = this._activeTasks.asReadonly();
  readonly selectedChunks = this._selectedChunks.asReadonly();
  readonly isMultiSelectMode = this._isMultiSelectMode.asReadonly();
  readonly content = this._content.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly lectureFinished = this._lectureFinished.asReadonly();
  readonly playerState = this._playerState.asReadonly();
  readonly currentChunk = this._currentChunk.asReadonly();
  readonly playerCurrentTime = this._playerCurrentTime.asReadonly();
  readonly highlightedChunkId = this._highlightedChunkId.asReadonly();
  readonly playbackCommand = this._playbackCommand.asReadonly();
  readonly isGeneratorOpen = signal(false);
  readonly uploadState = this._uploadState.asReadonly();
  readonly liveTranscriptionSource = this._liveTranscriptionSource.asReadonly();
  readonly isLiveTranscribing = computed(() => this._liveTranscriptionSource() !== null);
  readonly isReader = this._isReader.asReadonly();
  readonly isContentEditor = this._isContentEditor.asReadonly();
  readonly isReadOnly = computed(() => !this.authService.isWriter() || this.isReader());
  readonly questionsAnalysisResult = this._questionsAnalysisResult.asReadonly();
  readonly isSearchOpen = this._isSearchOpen.asReadonly();
  readonly searchResults = this._searchResults.asReadonly();
  readonly playerProgress = computed<number>(() => {
    const currentTime = this.playerCurrentTime();
    const duration = this.playerState().duration || 0;
    return duration > 0 ? (currentTime / duration) * 100 : 0;
  });

  // =============== COMPUTED SIGNALS ===============

  private readonly _currentChunkIndex = computed(() => {
    const content = this.content();
    const currentChunkId = this.currentChunk()?._id;
    if (!currentChunkId) return -1;
    return content.findIndex((c) => c._id === currentChunkId);
  });

  readonly hasNextChunk = computed(() => {
    const currentIndex = this._currentChunkIndex();
    return currentIndex > -1 && currentIndex < this.content().length - 1;
  });

  readonly hasPreviousChunk = computed(() => {
    const currentIndex = this._currentChunkIndex();
    return currentIndex > 0;
  });

  readonly isReadyForIndexing = computed(() => this.content().some((chunk) => chunk.isReadyForIndexing));
  readonly hasChunkWithCreationErrors = computed(() => this.content().some((chunk) => chunk?.error === 'creation'));

  // =============== PRIVATE PROPERTIES ===============
  public contentId: string | undefined;
  public shareToken: string | undefined;
  public courseId: string | undefined;
  public blogId: string | undefined;
  public noteId: string | undefined;
  public contentName: string | undefined;
  public contentType: string | undefined;
  public section: any;
  public lecture: any;
  public notificationToken: unknown;
  private readonly playbackRates = [0.5, 0.7, 1, 1.2, 1.5];
  private languageChanged = false;

  // Add a property to store the subscription
  private updatedCacheDataSubscription: Subscription | null = null;
  private firestoreContentSubscription: Subscription | null = null;
  private taskSubscription: Subscription | null = null;
  private playSubscription: Subscription | null = null;

  public stopLiveTranscriptionCommand$ = new Subject<void>();

  public triggerStopLiveTranscription() {
    this.stopLiveTranscriptionCommand$.next();
  }

  // Share live audio stream globally so header indicators can pulse in any instance
  liveAudioStream = signal<MediaStream | null>(null);

  // =============== INDEXING DEBOUNCE ===============
  private readonly _triggerIndexSubject = new Subject<void>();
  private _indexSubscription: Subscription | null = null;
  private readonly document = inject(DOCUMENT);

  constructor() {
    // React to audio speed changes from the CourseDisplayerService
    this.cdService.audioSpeed$.pipe(takeUntilDestroyed()).subscribe((speed) => {
      this._playerState.set({ ...this._playerState(), playbackRate: speed });
    });

    // React to content update events
    this.updatedCacheDataSubscription = this.storageService.updatedCacheData$
      .pipe(
        takeUntilDestroyed(),
        filter((update) => update.table === 'contents' && update.id[0] === this.contentId)
      )
      .subscribe(() => {
        if (this.isLiveTranscribing()) {
          console.warn('[ContentStateService] Skipping cache update refresh because live transcription is active to prevent UI refresh interruptions.');
          return;
        }
        this.contentService.getContent(this.contentId).then((res: any) => {
          this.setContent(res);
        });
      });

    this.translate.onLangChange.pipe(takeUntilDestroyed()).subscribe(() => {
      this.languageChanged = true;
      this.setHighlightedChunkId(null);
      if (!this.isLiveTranscribing()) {
        this.translateChunksIfNeeded(this.translate.currentLang);
        this.languageChanged = false;
      }
    });

    this.notificationService.embeddingGenerationComplete$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((contentId) => contentId && contentId === this.contentId)
      )
      .subscribe(() => {
        // Embeddings are a backend state used for search; we do not need to refresh the UI and disrupt the user.
        console.log('[ContentStateService] Embedding generation complete for contentId:', this.contentId);
      });

    this.notificationService.newChunkReceived$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((data) => data && data.contentID === this.contentId)
      )
      .subscribe((data) => {
        const chunkId = data.chunkId;
        let alternateID: string | undefined;
        try {
          if (data.data) {
            const parsedData = JSON.parse(data.data);
            alternateID = parsedData.alternateID;
          }
        } catch (e) {
          // Ignore JSON parsing errors
        }

        const alreadyExistsIndex = this.content().findIndex((c) => c._id === chunkId || (alternateID && c._id === alternateID) || c.alternateID === chunkId);

        if (alreadyExistsIndex > -1) {
          try {
            if (data.data) {
              const updatedChunk = JSON.parse(data.data);

              // Update local Dexie cache so components/pipes reading from it re-evaluate
              this.storageService.store('chunks', updatedChunk);

              const mainID = updatedChunk.mains?.find((e: any) => e.language === this.translate.currentLang) || updatedChunk.mains?.[0] || {};
              const mainEdition = updatedChunk.editions?.find((e: any) => mainID.main === e._id) || updatedChunk.editions?.[0];

              this._content.update((current) => {
                const newContent = [...current];
                const existing = newContent[alreadyExistsIndex];

                newContent[alreadyExistsIndex] = {
                  ...existing,
                  ...updatedChunk,
                  mainEdition: mainEdition ? { ...mainEdition, type: updatedChunk.type } : existing.mainEdition,
                };
                return newContent;
              });

              if (updatedChunk.status === 'ready' && this.highlightedChunkId() === chunkId && ['html', 'canvas-transcript'].includes(updatedChunk.type)) {
                const currentTitle = updatedChunk.title || this.translate.instant('Global.Transcript');
                this.openCanvas({
                  title: currentTitle,
                  html: mainEdition?.content || '',
                  source: 'transcript',
                  chunkId: chunkId,
                  contentId: this.contentId,
                  edition: mainEdition,
                });

                this.openContentGenerator({
                  initialSubject: currentTitle,
                  context: {
                    id: chunkId,
                    title: currentTitle,
                    name: currentTitle,
                    type: 'chunk',
                  },
                  isAuto: true,
                });
              } else if (updatedChunk.status === 'failed' && this.highlightedChunkId() === chunkId) {
                this.openCanvas({
                  title: this.translate.instant('Global.Transcript'),
                  html: `<p><i>${this.translate.instant('Content.Chunk.TranscriptionFailed')}</i></p>`,
                  source: 'transcript',
                  chunkId: chunkId,
                  contentId: this.contentId,
                  edition: mainEdition,
                });
              }
            }
          } catch (e) {
            console.error('Error updating existing chunk from notification', e);
          }
          return;
        }
        // New chunk: add it directly from notification data if available
        if (data.data) {
          try {
            const newChunk = JSON.parse(data.data);

            // Update local Dexie cache
            this.storageService.store('chunks', newChunk);

            const mainID = newChunk.mains?.find((e: any) => e.language === this.translate.currentLang) || newChunk.mains?.[0] || {};
            const mainEdition = newChunk.editions?.find((e: any) => mainID.main === e._id) || newChunk.editions?.[0];

            this._content.update((current) => [
              ...current,
              {
                ...newChunk,
                mainEdition: mainEdition ? { ...mainEdition, type: newChunk.type } : undefined,
                pieceId: `p_${newChunk._id}`,
                index: current.length,
              },
            ]);
          } catch (e) {
            console.error('Error adding new chunk from notification, falling back to getPieces', e);
            this.getPieces(true, true);
          }
        } else {
          this.getPieces(true, true);
        }
        this.toast.snackbar.dismiss();
      });

    // =============== DEBOUNCED INDEXING LOGIC ===============
    this._indexSubscription = this._triggerIndexSubject
      .pipe(
        debounceTime(5000), // 5 seconds debounce
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        if (this.isReadyForIndexing() && this.contentId) {
          this.contentService.generateEmbeddingsForContentChunks(this.contentId, true);
        }
      });

    effect(() => {
      if (this.isReadyForIndexing()) {
        this._triggerIndexSubject.next();
      }
    });

    this.flushIndexingOnExit = this.flushIndexingOnExit.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);

    // Bind to all exit lifecycle events for maximum browser coverage
    this.document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.document.defaultView?.addEventListener('pagehide', this.flushIndexingOnExit);
    this.document.defaultView?.addEventListener('beforeunload', this.flushIndexingOnExit);

    this.destroyRef.onDestroy(() => {
      this.document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      this.document.defaultView?.removeEventListener('pagehide', this.flushIndexingOnExit);
      this.document.defaultView?.removeEventListener('beforeunload', this.flushIndexingOnExit);

      // Flush immediately if service is somehow destroyed within the app workflow
      if (this.isReadyForIndexing() && this.contentId) {
        this.flushIndexingOnExit();
      }
    });
  }

  private handleVisibilityChange(): void {
    if (this.document.visibilityState === 'hidden') {
      this.flushIndexingOnExit();
    }
  }

  private flushIndexingOnExit(event?: Event): void {
    if (this.isReadyForIndexing() && this.contentId) {
      const chunkIds = this.content()
        .filter((c: any) => c.isReadyForIndexing)
        .map((c: any) => c._id)
        .filter((chunkId: string) => !chunkId.includes('temp'));

      if (chunkIds.length === 0) return;

      const baseUrl = this.envService.apiUrl + 'chunks/generate-embeddings-for-ready-chunks';
      const loginDataStr = localStorage.getItem('loginData');
      const token = loginDataStr ? JSON.parse(loginDataStr).token : null;

      // Build URL with all required query params that the HTTP interceptor normally provides.
      // sendBeacon bypasses Angular's interceptor, so we must replicate these manually.
      const appName = this.envService.appName;
      const tenantId = localStorage.getItem('active_workspace') || appName;
      const params = new URLSearchParams();
      if (token) params.set('auth_token', token);
      if (appName) params.set('app_name', appName);
      if (tenantId) params.set('tenantId', tenantId);
      const url = `${baseUrl}?${params.toString()}`;
      const payload = JSON.stringify({ chunkIds, contentId: this.contentId });

      try {
        // Attempt 1: Beacon with raw string (defaults to text/plain;charset=UTF-8)
        // This is perfectly compatible with the backend's bodyParser.text()
        const sent = navigator.sendBeacon(url, payload);

        // Attempt 2: If Beacon failed (e.g. adblocker or unsupported), use fetch keepalive
        if (!sent) {
          fetch(url, {
            method: 'POST',
            keepalive: true,
            headers: { 'Content-Type': 'text/plain' },
            body: payload,
          }).catch(() => {});
        }
      } catch (e) {
        // Fallback catch
      }
    }
  }

  /**
   * Initializes the service with necessary IDs to fetch and manage content.
   * @param config - The configuration object with IDs.
   */
  initialize(
    config: {
      contentId?: string;
      courseId?: string;
      blogId?: string;
      section?: any;
      lecture?: any;
      noteId?: string;
      contentName?: string;
      contentType?: string;
      shareToken?: string;
    },
    hardReload = false
  ): void {
    this.contentId = config.contentId;
    this.shareToken = config.shareToken;
    this.courseId = config.courseId;
    this.blogId = config.blogId;
    this.section = config.section;
    this.lecture = config.lecture;
    this.noteId = config.noteId;
    this.contentName = config.contentName;
    this.contentType = config.contentType;

    if (this.shareToken) {
      this.setReaderState(true);
    }

    this.getPieces(hardReload, hardReload);
    if (this.contentId && !this.shareToken) {
      // FCM Disabled - Switched to Firestore Event Bus
      // this.subscribeToNotifications();
      this.startFirestoreContentListener();
    }
    if (!this.shareToken) {
      this.initializeContentIfNeeded();
      this.notificationService.listenToMessages();
    }
  }

  /**
   * Clears the current content state and forcefully unsubscribes from active real-time events.
   * Call this during ngOnDestroy in container components (Courses, Notes).
   */
  clear(): void {
    if (this.firestoreContentSubscription) {
      this.firestoreContentSubscription.unsubscribe();
      this.firestoreContentSubscription = null;
    }
    if (this.taskSubscription) {
      this.taskSubscription.unsubscribe();
      this.taskSubscription = null;
    }
    if (this.playSubscription) {
      this.playSubscription.unsubscribe();
      this.playSubscription = null;
    }
    this.contentId = undefined;
    this._content.set([]);
  }

  openAIPane(data: AIPaneData): void {
    this._openAIPaneCommand.next(data);
  }

  private readonly _openQuizGeneratorCommand = new Subject<any>();
  readonly openQuizGeneratorCommand$ = this._openQuizGeneratorCommand.asObservable();

  openQuizGenerator(data: any): void {
    this._openQuizGeneratorCommand.next(data);
  }

  openCanvas(data: any): void {
    this._openCanvasCommand.next(data);
  }

  openWhiteboardEditor(data: any): void {
    this._openWhiteboardEditorCommand.next(data);
  }

  updateCanvasContent(contentId: string, messageId: string, content: any): Observable<any> {
    if (this.isReadOnly()) return of(null);
    return this.conversationService.updateMessage(contentId, messageId, content);
  }

  // =============== CONTENT GENERATOR COMMANDS ===============

  private readonly _openContentGeneratorCommand = new Subject<
    boolean | { initialSubject: string; mediaContext?: { url?: string; file?: File; type: 'audio' | 'file' | 'video' | 'image'; filename?: string }; insertAt?: any }
  >();
  readonly openContentGeneratorCommand$ = this._openContentGeneratorCommand.asObservable();

  private readonly _openContentGeneratorDraftCommand = new Subject<any>();
  readonly openContentGeneratorDraftCommand$ = this._openContentGeneratorDraftCommand.asObservable();

  // =============== IMAGE GENERATOR COMMANDS ===============

  private readonly _openImageGeneratorCommand = new Subject<void>();
  readonly openImageGeneratorCommand$ = this._openImageGeneratorCommand.asObservable();

  private readonly _openImageRefinerCommand = new Subject<any>();
  readonly openImageRefinerCommand$ = this._openImageRefinerCommand.asObservable();

  openImageGenerator(): void {
    this._openImageGeneratorCommand.next();
  }

  openImageRefiner(draft: any): void {
    this._openImageRefinerCommand.next(draft);
  }

  // =============== MERMAID GENERATOR COMMANDS ===============
  private readonly _openMermaidGeneratorCommand = new Subject<boolean>();
  readonly openMermaidGeneratorCommand$ = this._openMermaidGeneratorCommand.asObservable();

  openMermaidGenerator(): void {
    this._openMermaidGeneratorCommand.next(true);
  }

  closeMermaidGenerator(): void {
    this._openMermaidGeneratorCommand.next(false);
  }

  // =============== FILE VIEWER COMMANDS ===============
  private readonly _openFileViewerCommand = new Subject<{ fileLink: string; fileName: string; fileType: string }>();
  readonly openFileViewerCommand$ = this._openFileViewerCommand.asObservable();

  openFileViewer(data: { fileLink: string; fileName: string; fileType: string }): void {
    this._openFileViewerCommand.next(data);
  }

  // =============== CODE EDITOR COMMANDS ===============
  private readonly _openCodeEditorCommand = new Subject<{ contentId: string; chunkId: string; title: string; content: string; codeLanguage: string; edition: any }>();
  readonly openCodeEditorCommand$ = this._openCodeEditorCommand.asObservable();

  openCodeEditor(data: { contentId: string; chunkId: string; title: string; content: string; codeLanguage: string; edition: any }): void {
    this._openCodeEditorCommand.next(data);
  }

  // Store options temporarily so the component can pick them up on init
  private readonly _pendingGeneratorOptions = signal<{
    initialSubject: string;
    mediaContext?: { url?: string; file?: File; type: 'audio' | 'file' | 'video' | 'image'; filename?: string };
    context?: any; // ContentSearchResult
    insertAt?: any;
  } | null>(null);
  readonly pendingGeneratorOptions = this._pendingGeneratorOptions.asReadonly();

  openContentGenerator(data?: {
    initialSubject: string;
    mediaContext?: { url?: string; file?: File; type: 'audio' | 'file' | 'video' | 'image'; filename?: string };
    context?: any;
    insertAt?: any;
    isAuto?: boolean;
  }): void {
    if (data) {
      this._pendingGeneratorOptions.set(data);
    }
    this._openContentGeneratorCommand.next(data || true);
  }

  closeContentGenerator(): void {
    this._openContentGeneratorCommand.next(false);
  }

  consumePendingGeneratorOptions() {
    const options = this._pendingGeneratorOptions();
    this._pendingGeneratorOptions.set(null);
    return options;
  }

  openContentGeneratorDraft(draft: any): void {
    this._openContentGeneratorDraftCommand.next(draft);
  }

  // =============== CONTENT REFINER COMMANDS ===============
  private readonly _openContentRefinerCommand = new Subject<any>();
  readonly openContentRefinerCommand$ = this._openContentRefinerCommand.asObservable();

  openContentRefiner(draft: any): void {
    this._openContentRefinerCommand.next(draft);
  }

  /**
   * Returns the full text content of the current lecture/document.
   * Filters out non-text chunks if necessary and concatenates content.
   */
  getFullContentText(): string {
    const chunks = this.content();
    if (!chunks || chunks.length === 0) return '';
    const currentLang = this.translate.currentLang;

    return chunks
      .map((chunk) => {
        let text = '';

        // Try to find content in editions first
        if (chunk.editions && Array.isArray(chunk.editions) && chunk.editions.length > 0) {
          const edition = chunk.editions.find((e) => e.language === currentLang) || chunk.editions[0];
          if (edition && edition.content) {
            text = edition.content;
          }
        }

        // Fallback to root properties if editions didn't yield result
        if (!text) {
          text = chunk.text || chunk.html || chunk.content || '';
        }

        return text;
      })
      .filter((text) => !!text)
      .join('\n\n');
  }

  addTextToEditor(text: string): void {
    this._addTextToEditorCommand.next(text);
  }

  createChunkWithText(text: string): void {
    this._createChunkWithTextCommand.next(text);
  }

  /**
   * Sets the highlighted chunk ID.
   * @param chunkId - The ID of the chunk to highlight, or undefined to clear.
   */
  setHighlightedChunkId(chunkId: string | null | undefined): void {
    this._highlightedChunkId.set(chunkId);
  }

  /**
   * Sets the loading state of the service.
   * @param loadingState - The new loading state.
   */
  setLoading(loadingState: LoadingState): void {
    this._loading.set(loadingState);
  }

  /**
   * Updates a chunk in the local state.
   * @param chunkId - The ID of the chunk to update.
   * @param data - The data to merge into the chunk.
   */
  updateChunkLocally(chunkId: string, data: any): void {
    this._content.update((current) => current.map((c) => (c._id === chunkId ? { ...c, ...data } : c)));
  }

  /**
   * Inserts chunks locally into the state.
   * @param chunks - The chunks to insert.
   * @param insertAfterId - The ID of the chunk to insert after.
   */
  insertChunksLocally(chunks: any[], insertAfterId: string): void {
    this._content.update((current) => {
      const index = current.findIndex((c) => c._id === insertAfterId);
      if (index > -1) {
        const newContent = [...current];
        newContent.splice(index + 1, 0, ...chunks);
        return newContent.map((c, i) => ({ ...c, index: i }));
      }
      return [...current, ...chunks].map((c, i) => ({ ...c, index: i }));
    });
  }

  /**
   * Toggles selection for a chunk.
   * @param chunkId - The ID of the chunk to toggle.
   */
  toggleChunkSelection(chunkId: string): void {
    this._isMultiSelectMode.set(true);
    this._selectedChunks.update((selected) => {
      if (selected.includes(chunkId)) {
        return selected.filter((id) => id !== chunkId);
      }
      return [...selected, chunkId];
    });
  }

  /**
   * Selects all chunks from a given list.
   * @param chunkIds - Array of chunk IDs to select.
   */
  selectAllChunks(chunkIds: string[]): void {
    this._isMultiSelectMode.set(true);
    this._selectedChunks.set([...chunkIds]);
  }

  /**
   * Clears all chunk selections without exiting multi-select mode.
   */
  clearChunkSelection(): void {
    this._selectedChunks.set([]);
  }

  /**
   * Clears chunk selections and exits multi-select mode.
   */
  exitMultiSelectMode(): void {
    this._isMultiSelectMode.set(false);
    this._selectedChunks.set([]);
  }

  /**
   * Sets the upload state of the service.
   * @param uploadState - The new upload state.
   */
  setUploadState(uploadState: UploadState | null): void {
    this._uploadState.set(uploadState);
  }

  setLiveTranscriptionState(isTranscribing: boolean, source?: 'recorder' | 'chunk' | 'dictation'): void {
    if (isTranscribing) {
      this._liveTranscriptionSource.set(source || 'recorder');
    } else {
      this._liveTranscriptionSource.set(null);
    }
  }

  setReaderState(isReader: boolean): void {
    this._isReader.set(isReader);
  }

  setContentEditorState(isEditor: boolean): void {
    this._isContentEditor.set(isEditor);
  }

  setQuestionsAnalysisResult(result: any): void {
    this._questionsAnalysisResult.set(result);
  }

  openQuestionsAnalysisSidebar(open: boolean): void {
    this._openQuestionsAnalysisSidebarCommand.next(open);
  }

  openQuestionsSearchSidebar(open: boolean): void {
    this._openQuestionsSearchSidebarCommand.next(open);
  }

  setSearchResults(results: any): void {
    this._searchResults.set(results);
  }

  setSearchOpen(isOpen: boolean): void {
    this._isSearchOpen.set(isOpen);
  }

  /**
   * Main data fetching method.
   * @param isReload - Forces a reload from the network.
   * @param hardReload - Bypasses cache entirely.
   */
  async getPieces(isReload = false, hardReload = false): Promise<void> {
    if (this.isLiveTranscribing()) {
      console.warn('[ContentStateService] Skipping getPieces because live transcription is active to prevent UI refresh interruptions.');
      return;
    }

    if (this.isFetchingPieces) {
      return;
    }
    this.isFetchingPieces = true;
    this.setLoading({ status: 'loading', message: this.translate.instant('Loader.FetchingData'), reason: 'fetch-data' });
    if (!this.contentId) {
      this._content.set([]);
      this.setLoading({ status: 'idle' });
      this.isFetchingPieces = false;
      return;
    }

    try {
      const res: any = this.shareToken
        ? await firstValueFrom(this.mySpaceService.getPublicContent(this.shareToken, this.contentId))
        : await this.contentService.getContent(this.contentId, hardReload);
      this.setContent(res);
      this.setLoading({ status: 'success', reason: 'fetch-data' });
      setTimeout(() => this.setLoading({ status: 'idle' }), 1500);
    } catch (error) {
      console.error('Failed to get pieces:', error);
      this._content.set([]);
      this.setLoading({ status: 'error', message: this.translate.instant('Loader.FetchingError'), reason: 'fetch-data' });
      setTimeout(() => this.setLoading({ status: 'idle' }), 5000);
    } finally {
      this.isFetchingPieces = false;
    }
  }

  private setContent(res: any): void {
    if (!res?.content?.contents) {
      this._content.set([]);
      return;
    }

    const currentChunkId = this.currentChunk()?._id;

    const newContent: Chunk[] = res.content.contents.map((chunk: any, index: number) => ({
      ...this.processChunkAudio(chunk),
      pieceId: `p_${chunk._id}`,
      index,
    }));

    this._content.set(newContent);

    const newCurrentIndex = newContent.findIndex((chunk) => chunk._id === currentChunkId);

    if (newCurrentIndex > -1) {
      this._currentChunk.set(newContent[newCurrentIndex]);
      this.cdService.setCurrentChunkIndex(newCurrentIndex, newContent[newCurrentIndex]);
    } else {
      this._currentChunk.set(newContent[0]);
      this.cdService.setCurrentChunkIndex(0, newContent[0]);
    }

    if (newContent.length > 0) {
      this.cdService.playButtonIsAvailable(true);
    }
    this.fetchFinishedStatus();

    if (this.languageChanged) {
      this.translateChunksIfNeeded(this.translate.currentLang);
      this.languageChanged = false;
    }
  }

  private processChunkAudio(chunk: any): any {
    const silentAudioDuration = 3;
    if (!chunk.hasAudio && (chunk.type === 'file' || chunk.type === 'image' || chunk.type === 'text' || chunk.type === 'quiz')) {
      const duration = chunk.audioMustEndAt > 0 ? chunk.audioMustEndAt : silentAudioDuration;
      return {
        ...chunk,
        audio: this.sanitizer.bypassSecurityTrustResourceUrl(createSilentAudio(duration)),
        hasAudio: true,
        hasSilentAudio: true,
      };
    }
    return chunk;
  }

  private translateChunksIfNeeded(targetLanguage: string): void {
    const chunks = this.content();
    if (!chunks || chunks.length === 0 || !this.contentId) {
      return;
    }

    const sourceLanguage = targetLanguage === 'fr' ? 'en' : 'fr';

    const chunksToTranslate = chunks.filter((chunk) => {
      if (chunk.type !== 'text' && chunk.type !== 'html') {
        return false;
      }
      const targetEdition = chunk.editions?.find((ed) => ed.language === targetLanguage);
      return !targetEdition || chunk.__v > targetEdition.__v;
    });

    if (chunksToTranslate.length === 0) {
      return;
    }

    this.toast.snackbar.open(this.translate.instant('Snackbar.TranslationStarted'), 'OK', { duration: 3000 });

    const translationObservables = chunksToTranslate.map((chunk) => {
      const sourceEdition = chunk.editions?.find((ed) => ed.language === sourceLanguage);
      const sourceVersion = sourceEdition?.__v || 1;

      return this.chunkService.translateChunk({
        chunkId: chunk._id,
        version: sourceVersion,
        editions: chunk.editions || [],
        type: chunk.type,
        targetLanguage: targetLanguage,
        contentId: this.contentId,
      });
    });

    forkJoin(translationObservables).subscribe({
      error: (err) => {
        const msg = err.error?.messageCode || err.error?.msg;
        if (msg === 'notEnoughCredits') {
          this.toast.snackbar.openFromComponent(NotEnoughCreditComponent, {
            data: {
              message: this.translate.instant('Toastr.notEnoughCredits'),
              linkText: this.translate.instant('Toastr.addCreditsHere'),
              linkUrl: '/myspace/credit',
            },
            duration: 10000,
          });
        } else {
          this.toast.snackbar.open(this.translate.instant('Snackbar.TranslationFail'), 'OK', { duration: 2000 });
        }
      },
    });
  }

  /**
   * Saves the new order of chunks after a drag-and-drop operation.
   * @param content - The reordered array of chunks.
   */
  async saveContentOrder(content: Chunk[]): Promise<void> {
    if (this.isReadOnly()) {
      return;
    }
    const contentChunkRef: ContentChunkRef[] = content.map((c) => ({
      _id: c._id, // will overwrite the existing _id but this is fine it is basically a dummy ID
      style: 'paragraph',
      chunk: c._id, // Assuming chunk is the ID reference to ServerChunk
    }));

    try {
      await firstValueFrom(
        this.contentService.contentEditChunks({
          _id: this.contentId,
          contents: contentChunkRef,
        })
      );
    } catch (err) {
      console.error('Failed to save content order:', err);
      this.getPieces(true);
    }
  }

  /**
   * Marks the current lecture as finished.
   */
  markAsFinished(): void {
    if (this.lecture) {
      this.mySpaceService.passLectureEvent({
        courseID: this.courseId,
        sectionID: this.section._id,
        lectureID: this.lecture._id,
        finished: true,
      });
      this._lectureFinished.set(true);
    }
  }

  /**
   * Handles the deletion of a chunk and reloads the content.
   */
  chunkDeleted(): void {
    this.getPieces(true, true);
  }

  /**
   * Re-initiates the creation process for chunks that failed.
   */
  recreateFailedChunks(): void {
    if (this.isReadOnly()) return;
    const chunksToRecreate = this.content().filter((c) => c.error === 'creation');
    this.chunkService.restartMultipleChunkCreationProcess(chunksToRecreate).then(() => {
      this.getPieces(true, true);
    });
  }

  uploadFile(file: File, insertId: string | null, insertAsFirst: boolean, insertAbove?: boolean): void {
    if (this.isReadOnly()) return;
    this._uploadState.set({
      progress: 0,
      status: 'uploading',
      insertId: insertId,
      fileName: file.name,
    });

    const params: any = {
      content: '',
      tags: [],
      courseID: this.courseId,
      blogID: this.blogId,
      noteID: this.noteId,
      lecture: this.lecture,
      section: this.section,
      contentID: this.contentId,
      contentName: this.contentName,
      contentType: this.contentType,
      isolateChunk: false,
      type: 'file',
      language: this.translate.currentLang,
      generateVoice: false,
      voiceGender: 'MALE',
      generateTranslation: false,
      generateEmbeddings: false,
      insertBelowID: insertAbove ? null : insertId,
      insertAboveID: insertAbove ? insertId : null,
    };

    if (insertAsFirst) {
      params.insertAsFirst = true;
    }

    this.uploadService.uploadFileWithProgress(file, params).subscribe({
      next: (progress: number) => {
        if (progress === 100) {
          this._uploadState.update((state) => (state ? { ...state, progress, status: 'uploading' } : null));
          setTimeout(() => {
            this._uploadState.update((state) => (state ? { ...state, status: 'creating' } : null));
          }, 50);
        } else this._uploadState.update((state) => (state ? { ...state, progress, status: 'uploading' } : null));
      },
      error: (err) => {
        console.error('Upload failed', err);
        this._uploadState.update((state) => (state ? { ...state, status: 'failed' } : null));
        setTimeout(() => this._uploadState.set(null), 5000);
      },
      complete: () => {
        setTimeout(() => {
          this._uploadState.set(null);
        }, 50);
      },
    });
  }

  uploadRecording(file: Blob, type: 'video' | 'image', chunkAboveId: string | null | undefined): void {
    if (this.isReadOnly() || !this.contentId) return;
    const params: UploadParams = {
      contentID: this.contentId,
      blogID: this.blogId,
      courseID: this.courseId,
      contentName: this.contentName,
      insertBelowID: chunkAboveId || undefined,
      insertAsFirst: !chunkAboveId ? true : false,
    };

    const insertId = chunkAboveId ?? null;
    const aFile = file instanceof File ? file : new File([file], `${uuidv4()}.${type === 'image' ? 'png' : 'mp4'}`, { type: file.type });
    const fileName = aFile.name;

    this.setUploadState({ status: 'uploading', progress: 0, insertId, fileName });

    this.uploadService.uploadFileWithProgress(aFile, params).subscribe({
      next: (progress) => {
        if (progress < 100) {
          this.setUploadState({ status: 'uploading', progress, insertId, fileName });
        } else {
          this.setUploadState({ status: 'creating', progress: 100, insertId, fileName });
        }
      },
      error: (err) => {
        this.errorHandler.handle401Error(err);
        this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Close', { duration: 2000 });
        this.setUploadState(null);
      },
      complete: () => {
        this.chunkService.passChunkEditEvent('all');
        this.setUploadState(null);
      },
    });
  }

  clearMainPlayer(): void {
    this.mainPlayerCommand.next('clear');
  }

  reportPlaybackStarted(chunkId: string): void {
    this.appService.setCreateMode(false);
    this._playerState.set({
      status: 'playing',
      chunkId: chunkId,
      sourceUrl: undefined, // Indicates playback is not in the main player
      currentTime: 0,
      playbackRate: this.playerState().playbackRate || 1,
    });
    this._currentChunk.set(this.content().find((c) => c._id === chunkId));
    this.setHighlightedChunkId(chunkId);
    this.clearMainPlayer();
  }

  reportPlaybackPaused(chunkId: string): void {
    // Only update if this chunk was the one playing
    if (this.playerState().chunkId === chunkId) {
      this._playerState.update((state) => ({ ...state, status: 'paused' }));
    }
  }

  // =============== AUDIO PLAYER LOGIC ===============

  clearPlaybackCommand(): void {
    this._playbackCommand.set(undefined);
  }

  /**
   * Cycles through the playback speed of the audio player.
   */
  cyclePlaybackRate(): void {
    const currentRate = this._playerState().playbackRate || 1;
    const currentIndex = this.playbackRates.indexOf(currentRate);
    const nextIndex = (currentIndex + 1) % this.playbackRates.length;
    const newRate = this.playbackRates[nextIndex];
    this._playerState.update((state) => ({ ...state, playbackRate: newRate }));
  }

  /**
   * Initiates playback of a specific chunk.
   * @param chunkId - The ID of the chunk to play.
   */
  async playTrack(chunkId: string, chunk?: Chunk, forceGeneration = false): Promise<void> {
    this.appService.setCreateMode(false);
    if (this.playSubscription) {
      this.playSubscription.unsubscribe();
      this.playSubscription = null;
    }

    let chunkToPlay: Chunk | undefined = chunk || this.content().find((c) => c._id === chunkId);
    if (!chunkToPlay) {
      return;
    }

    if (chunkToPlay.pieceId) {
      const chunkInMemory = await this.storageService.find('chunks', [chunkToPlay._id]);
      if (chunkInMemory.length > 0) {
        chunkToPlay = chunkInMemory[0];
      }
    }

    // is empty chunk
    const isEmptyChunk = ['<p></p><p></p>', '<p></p>', ''].includes(chunkToPlay.editions?.find((ed) => ed.language === this.translate.currentLang)?.content || '');

    // Handle programmatic playback for video and audio chunks
    if (chunkToPlay.type === 'audio' || chunkToPlay.type === 'video') {
      this._currentChunk.set(chunkToPlay);
      this.setHighlightedChunkId(chunkToPlay._id);
      this._playbackCommand.set({ chunkId: chunkToPlay._id, action: 'play' });
      this._playerState.update((s) => ({ ...s, status: 'stopped' })); // Stop main player
      this.prepareNextToPlay(chunkToPlay);
      return;
    }

    const audioToPlay = chunkToPlay.audios?.find((audio) => audio._id === this.translate.currentLang);
    const currentEdition = chunkToPlay.editions?.find((ed) => ed.language === this.translate.currentLang);
    const matchingVersion = currentEdition?.__v === audioToPlay?.__v;
    const silentAudioDuration = 2.5;

    if (isEmptyChunk) {
      this._currentChunk.set(chunkToPlay);
      this.setHighlightedChunkId(chunkToPlay._id);
      this._playerState.set({
        status: 'playing',
        sourceUrl: createSilentAudio(silentAudioDuration),
        chunkId: chunkToPlay._id,
        currentTime: 0,
        playbackRate: this.playerState().playbackRate || 1,
        duration: silentAudioDuration,
      });
      this.prepareNextToPlay(chunkToPlay);
      return;
    }

    if ((!audioToPlay || !matchingVersion) && SILENT_CHUNKS.includes(chunkToPlay.type) && !forceGeneration) {
      this._currentChunk.set(chunkToPlay);
      this.setHighlightedChunkId(chunkToPlay._id);
      this._playerState.set({
        status: 'playing',
        sourceUrl: createSilentAudio(silentAudioDuration),
        chunkId: chunkToPlay._id,
        currentTime: 0,
        playbackRate: this.playerState().playbackRate || 1,
        duration: silentAudioDuration,
      });
      this.prepareNextToPlay(chunkToPlay);
      return;
    } else if (!audioToPlay || !matchingVersion) {
      // Only attempt generation if the chunk has an edition in the current language
      const hasEdition = chunkToPlay.editions?.some((ed) => ed.language === this.translate.currentLang);
      if (!hasEdition) {
        this.prepareNextToPlay(chunkToPlay);
        return;
      }

      this._currentChunk.set(chunkToPlay);
      this.setHighlightedChunkId(chunkToPlay._id);
      this._playerState.set({
        status: 'buffering',
        chunkId: chunkToPlay._id,
        playbackRate: this.playerState().playbackRate || 1,
      });
      this.cdService.setAudioStatus({ status: 'buffering', content: chunkToPlay });

      const targetVersion = chunkToPlay.editions?.find((ed) => ed.language === this.translate.currentLang)?.__v || 1;
      const targetLang = this.translate.currentLang;

      const tasks = this.activeTasks();
      const isRemoteGenerating = tasks[chunkToPlay._id]?.audio?.status === 'generating';

      const update$ = this.notificationService.newChunkReceived$.pipe(
        filter((notification) => notification && notification.chunkId === chunkToPlay._id),
        map((notification) => {
          try {
            return JSON.parse(notification.data) as Chunk;
          } catch (e) {
            return null;
          }
        }),
        filter((updatedChunk): updatedChunk is Chunk => {
          if (!updatedChunk) return false;
          const audio = updatedChunk.audios?.find((a) => a._id === targetLang);
          return !!(audio && audio.__v === targetVersion);
        }),
        take(1),
        switchMap(() => this.chunkService.getChunk(chunkToPlay._id))
      );

      let obs$: Observable<Chunk>;
      if (isRemoteGenerating) {
        obs$ = update$;
      } else {
        const trigger$ = this.chunkService.generateAudio({
          chunkId: chunkToPlay._id,
          version: targetVersion,
          linkPrefix: this.envService.linkPrefix,
          editions: chunkToPlay.editions || [],
          audios: chunkToPlay.audios || [],
          targetLanguage: targetLang,
          contentId: this.contentId,
        });
        obs$ = trigger$.pipe(switchMap(() => update$));
      }

      this.playSubscription = obs$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (readyChunk) => {
          this.playTrack(readyChunk._id, readyChunk, forceGeneration);
        },
        error: (err) => {
          console.error('Error generating audio in playTrack:', err);
          this.toast.snackbar.open(this.translate.instant('Loader.AudioGenerationFailed'), 'Close', { duration: 3000 });
          this._playerState.set({ status: 'stopped' });
          this.cdService.setAudioStatus({ status: 'paused', content: chunkToPlay });
        },
      });
      return;
    }

    const currentState = this.playerState();
    const isResuming = currentState.chunkId === chunkToPlay?._id;

    this.prepareNextToPlay(chunkToPlay);
    const storageRef = ref(this.fireStorage, audioToPlay.fileLink);
    from(getDownloadURL(storageRef)).subscribe({
      next: (url) => {
        this._currentChunk.set(chunkToPlay);
        this.setHighlightedChunkId(chunkToPlay._id);
        this._playerState.set({
          status: 'playing',
          sourceUrl: url,
          chunkId: chunkToPlay._id,
          currentTime: audioToPlay.currentTime || 0,
          playbackRate: currentState.playbackRate || 1,
          duration: isResuming ? currentState.duration : undefined,
        });
      },
      error: (err) => console.error('Error fetching download URL:', err),
    });
  }

  /**
   * Sets the playback speed of the audio player to a specific value.
   * @param rate The specific playback rate to set.
   */
  setSpecificPlaybackRate(rate: number): void {
    this._playerState.update((state) => ({ ...state, playbackRate: rate }));
  }

  /**
   * Pauses the currently playing audio.
   */
  pauseAudio(): void {
    if (this.playSubscription) {
      this.playSubscription.unsubscribe();
      this.playSubscription = null;
    }
    this._playerState.update((state) => ({ ...state, status: 'paused' }));
    const current = this.currentChunk();
    if (current && (current.type === 'audio' || current.type === 'video')) {
      this._playbackCommand.set({ chunkId: current._id, action: 'pause' });
    } else {
      this._playbackCommand.set(undefined);
    }
    this.cdService.setAudioStatus({ status: 'paused', content: this.currentChunk() });
  }

  clearAudio(): void {
    if (this.playSubscription) {
      this.playSubscription.unsubscribe();
      this.playSubscription = null;
    }
    this._playerState.set({ status: 'stopped', sourceUrl: undefined, chunkId: undefined, currentTime: 0, duration: undefined });
    this.cdService.resetAudioStatus();
    this._playerCurrentTime.set(0);
    this._playbackCommand.set(undefined);
  }

  /**
   * Prepares the next chunk to play. Send a notification through a subject.
   * This method is called when the current chunk starts playing.
   */
  async prepareNextToPlay(currentChunk: Chunk): Promise<void> {
    const currentIndex = this.content().findIndex((c) => c._id === currentChunk._id);
    if (currentIndex === -1) return;

    // Prefetch up to next 3 chunks
    for (let offset = 1; offset <= 3; offset++) {
      const nextIndex = currentIndex + offset;
      if (nextIndex >= this.content().length) break;

      let nextChunk = this.content()[nextIndex];
      if (nextChunk?.pieceId) {
        // Fix to retrieve chunk from cache/Dexie memory rather than references
        const chunkInMemory = await this.storageService.find('chunks', [nextChunk._id]);
        if (chunkInMemory.length > 0) nextChunk = chunkInMemory[0];
      }

      if (!nextChunk || CHUNKS_TO_SKIP_AUDIO_GEN.includes(nextChunk.type)) {
        continue;
      }

      const isEmptyChunk = ['<p></p><p></p>', '<p></p>', ''].includes(nextChunk.editions?.find((ed) => ed.language === this.translate.currentLang)?.content || '');
      if (isEmptyChunk) {
        continue;
      }

      const audioToPlay = nextChunk.audios?.find((audio) => audio._id === this.translate.currentLang);
      const matchingVersion = nextChunk.editions?.find((ed) => ed.language === this.translate.currentLang)?.__v === audioToPlay?.__v;

      if (audioToPlay && matchingVersion) {
        continue;
      }

      // Don't attempt generation if the chunk has no edition in the current language
      const hasEdition = nextChunk.editions?.some((ed) => ed.language === this.translate.currentLang);
      if (!hasEdition) {
        continue;
      }

      this.chunkService
        .generateAudio({
          chunkId: nextChunk._id,
          version: nextChunk.editions?.find((ed) => ed.language === this.translate.currentLang)?.__v || 1,
          linkPrefix: this.envService.linkPrefix,
          editions: nextChunk.editions || [],
          audios: nextChunk.audios || [],
          targetLanguage: this.translate.currentLang,
          contentId: this.contentId,
        })
        .subscribe({
          next: () => {},
          error: (err) => {
            console.error(`Error generating audio for next chunk at index ${nextIndex}:`, err);
          },
        });
    }
  }

  /**
   * Plays the next chunk in the content list.
   */
  playNext(nextChunk: Chunk): void {
    this.playTrack(nextChunk._id);
  }

  playNextChunk(): void {
    if (!this.hasNextChunk()) return;
    const nextChunk = this.content()[this._currentChunkIndex() + 1];
    if (!CHUNKS_WITHOUT_AUDIO_PLAYER.includes(nextChunk.type)) this.clearMainPlayer();
    if (CHUNKS_WITH_AUDIO_PLAYER.includes(this.currentChunk().type)) {
      this._playbackCommand.set({ chunkId: this.currentChunk()._id, action: 'pause' });
    }
    this.playTrack(nextChunk._id, nextChunk);
  }

  playPreviousChunk(): void {
    if (!this.hasPreviousChunk()) return;
    const previousChunk = this.content()[this._currentChunkIndex() - 1];
    if (!CHUNKS_WITHOUT_AUDIO_PLAYER.includes(previousChunk.type)) this.clearMainPlayer();
    if (CHUNKS_WITH_AUDIO_PLAYER.includes(this.currentChunk().type)) {
      this._playbackCommand.set({ chunkId: this.currentChunk()._id, action: 'pause' });
    }
    this.playTrack(previousChunk._id, previousChunk);
  }

  /**
   * Called by the component when the audio element's time updates.
   * @param currentTime - The current playback time from the audio element.
   */
  handleTimeUpdate(currentTime: number): void {
    if (!this._playerState().sourceUrl) return;

    const chunk = this.currentChunk();
    if (!chunk) return;

    this._playerCurrentTime.set(currentTime);

    // Check if a snippet needs to end early
    if (chunk.audioMustEndAt && currentTime > chunk.audioMustEndAt) {
      this.handleAudioEnded();
    }
  }

  /**
   * Called by the component when the audio track finishes playing.
   */
  handleAudioEnded(): void {
    this._playbackCommand.set(undefined);
    const currentIndex = this.content().findIndex((c) => c._id === this.currentChunk()?._id);
    if (currentIndex > -1 && currentIndex < this.content().length - 1) {
      const nextChunk = this.content()[currentIndex + 1];
      this.playNext(nextChunk);
    } else {
      this._playerState.set({ status: 'stopped' });
      this.cdService.setAudioStatus({ status: 'paused', content: this.currentChunk() });
    }
  }

  /**
   * Called by the component when the audio element's duration changes.
   * @param duration - The total duration of the audio element.
   */
  handleDurationChange(duration: number): void {
    this._playerState.update((state) => ({ ...state, duration }));
  }

  // =============== INITIALIZATION & NOTIFICATIONS ===============

  private async fetchFinishedStatus() {
    const data = this.courseId ? { courseID: this.courseId } : { blogID: this.blogId };
    if (data.blogID || data.courseID) {
      this.mySpaceService.getLectureProgress(data).subscribe((progress) => {
        // Complex logic to determine finished status would go here
        // For now, we assume it's a boolean or can be derived.
      });
    }
  }

  private async subscribeToNotifications() {
    this.notificationToken = await this.notificationService.getNotificationToken();
    if (this.notificationToken && this.contentId) {
      this.contentService.subscribeToNotifications({ token: this.notificationToken, contentID: this.contentId }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }
  }

  private startFirestoreContentListener() {
    if (this.firestoreContentSubscription) {
      this.firestoreContentSubscription.unsubscribe();
      this.firestoreContentSubscription = null;
    }
    if (this.taskSubscription) {
      this.taskSubscription.unsubscribe();
      this.taskSubscription = null;
    }

    const activeTenantId = localStorage.getItem('active_workspace') || this.authService.userInfo()?._id;
    if (activeTenantId && this.contentId) {
      this.firestoreContentSubscription = this.firestoreEventBus.listenToContentEvents(activeTenantId, this.contentId).subscribe((event) => {
        console.log(`[Firestore Event Bus - Content: ${this.contentId}] Payload received:`, event);
        this.notificationService.processNotification(event);
      });

      this.taskSubscription = this.firestoreEventBus.listenToContentStatus(activeTenantId, this.contentId).subscribe((docData) => {
        if (docData && docData.activeTasks) {
          this._activeTasks.set(docData.activeTasks);
        }
      });
    }
  }

  private initializeContentIfNeeded(): void {
    if (this.shareToken) return;
    if (!this.contentId && (this.courseId || this.blogId)) {
      this.contentService
        .courseAddContent({
          _id: this.courseId,
          sectionID: this.section._id,
          lectureID: this.lecture._id,
          blogID: this.blogId,
        })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((res) => {
          this.contentId = res.contentID;
          this.getPieces();
        });
    }
  }

  /**
   * Cleans up subscriptions and resources when the service is destroyed.
   * This is called automatically when the root injector is destroyed.
   */
  ngOnDestroy() {
    // Unsubscribe from updatedCacheDataSubscription
    if (this.updatedCacheDataSubscription) {
      this.updatedCacheDataSubscription.unsubscribe();
      this.updatedCacheDataSubscription = null;
    }

    if (this.taskSubscription) {
      this.taskSubscription.unsubscribe();
      this.taskSubscription = null;
    }

    if (this.playSubscription) {
      this.playSubscription.unsubscribe();
      this.playSubscription = null;
    }

    if (this.notificationToken && this.contentId) {
      navigator.sendBeacon(
        `${environment.apiUrl}contents/unsubscribe-notifications?app_name=${this.envService.appName}&userId=${this.authService.getID()}&token=${this.notificationToken}&contentID=${this.contentId}`
      );
    }
  }
}
